<template>
  <div :data-view-id="uniqueTitle" :unique-title="uniqueTitle">
    <ViewTitle :title="title" />
    <b-form v-if="phase === 'credentials' && loginFields.length" @submit="onSubmit" :id="uniqueTitle" novalidate>
      <div v-for="field in loginFields" :key="field.uniqueTitle" class="mb-4">
        <b-form-group :label="field.label" :label-for="field.uniqueTitle">
          <b-form-input
            :id="field.uniqueTitle"
            :name="field.uniqueTitle"
            :type="field.isPassword ? 'password' : 'text'"
            :autocomplete="field.isPassword ? 'new-password' : 'username'"
            :placeholder="field.hint || undefined"
            :required="field.required || undefined"
            v-model="fieldValues[field.uniqueTitle]"
          />
          <small v-if="field.description" class="text-muted d-block mt-1">{{ field.description }}</small>
        </b-form-group>
      </div>
      <p v-if="ruleSummary" class="text-muted">{{ ruleSummary }}</p>
      <b-button type="submit" variant="primary" :class="submittedClass">{{ submitText }}</b-button>
      <div v-if="errorMessage" class="text-danger mt-2" role="alert">{{ errorMessage }}</div>
    </b-form>
    <AuthenticatorPanel
      v-else-if="phase !== 'credentials'"
      :key="phase"
      :phase="phase"
      :otpauth-url="otpauthUrl"
      :secret="setupSecret"
      :recovery-codes="recoveryCodes"
      :error-message="errorMessage"
      :busy="submittedClass === 'disabled'"
      @submit="submitFactor"
      @continue="finishPending"
    />
  </div>
</template>

<script>
  import axios from 'axios'
  import ViewTitle from './ViewTitle.vue'
  import AuthenticatorPanel from './AuthenticatorPanel.vue'
  import { fieldNameAndHint } from '../../../shared/form-field-label.js'
import { isEmptyColumnValue } from '../../../shared/column-write.js'
import { recordFailures } from '../../../shared/column-validation.js'
import { passwordPolicyForRead, passwordRuleSummary } from '../../../shared/password-policy.js'
  import { listAuthInputs } from '../../../shared/auth-form-fields.js'
  import { completeSignIn } from '../lib/complete-sign-in'
  import { writeLoggedInUser } from '../lib/live-user-storage'
  import { pageHostUniqueTitle } from '../lib/page-host'

  function extractFields (formComponents) {

    return listAuthInputs({ components: formComponents }).map((comp) => {

      const named = fieldNameAndHint(comp.title, comp.placeholder || comp.uniqueTitle)

      const label = named.label || comp.uniqueTitle

      return {
        uniqueTitle: comp.uniqueTitle,
        label: comp.required ? `${label} *` : label,
        title: label,
        hint: named.placeholder,
        description: comp.description || '',
        required: !!comp.required,
        defaultValue: comp.defaultValue,
        columnType: comp.columnType || (comp.type === 'formPassword' ? 'password' : 'text'),
        validationRules: comp.validationRules || { rules: [] },
        isPassword: comp.type === 'formPassword'
      }
    })
  }

  export default {
    components: {
      ViewTitle,
      AuthenticatorPanel
    },
    props: ['formComponents', 'uniqueTitle', 'title', 'submissionRules', 'twoFactor'],
    data () {

      return {
        loginFields: [],
        fieldValues: {},
        submittedClass: '',
        submitText: 'create account',
        errorMessage: '',
        phase: 'credentials',
        setupToken: '',
        setupSecret: '',
        otpauthUrl: '',
        recoveryCodes: [],
        pendingSession: null,
        passwordPolicy: passwordPolicyForRead(null)
      }
    },
    computed: {
      ruleSummary () {

        return passwordRuleSummary(this.passwordPolicy)
      }
    },
    async mounted () {

      await this.loadPasswordPolicy()
    },
    watch: {
      formComponents: {
        immediate: true,
        handler () {

          this.loginFields = extractFields(this.formComponents)

          const values = {}

          for (const field of this.loginFields) {
            values[field.uniqueTitle] = isEmptyColumnValue(field.defaultValue) ? '' : field.defaultValue
          }

          this.fieldValues = values
        }
      },
      fieldValues: {
        deep: true,
        handler () {

          const started = Object.keys(this.fieldValues).some((key) => !isEmptyColumnValue(this.fieldValues[key]))

          if (!started) return

          const failures = recordFailures(this.validationColumns(), this.fieldValues, {
            passwordPolicy: this.passwordPolicy
          })

          this.errorMessage = failures.length ? failures[0].message : ''
        }
      }
    },
    methods: {
      async loadPasswordPolicy () {

        try {

          const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/settings`)

          this.passwordPolicy = passwordPolicyForRead(res.data && res.data.passwordPolicy)
        } catch (err) {

          this.passwordPolicy = passwordPolicyForRead(null)
        }
      },
      validationColumns () {

        return this.loginFields.map((field) => ({
          uniqueTitle: field.uniqueTitle,
          title: field.title || field.label,
          type: field.columnType || 'text',
          required: field.required,
          validationRules: field.validationRules
        }))
      },
      requestBody () {

        const body = {
          pageUniqueTitle: pageHostUniqueTitle(this.$el),
          componentUniqueTitle: this.uniqueTitle
        }

        for (const field of this.loginFields) body[field.uniqueTitle] = this.fieldValues[field.uniqueTitle]

        return body
      },
      async finishSession (data) {

        const redirectPath = await completeSignIn({
          username: data.username,
          userSession: data.userSession,
          submissionRules: this.submissionRules,
          fieldValues: this.fieldValues,
          modalRoute: this.modalRoute,
          account: this.routeAccount,
          application: this.$route.params.application,
          path: this.routePath || this.$route.path,
          accessKey: this.routeAccessKey,
          embed: this.routeEmbed,
          router: this.$router
        })

        if (redirectPath) return

        this.submittedClass = ''
        this.submitText = 'account created'
      },
      async finishPending () {

        if (!this.pendingSession) return

        await this.finishSession(this.pendingSession)
      },
      holdRecoveryCodes (data) {

        writeLoggedInUser(data.username, {
          account: this.routeAccount,
          application: this.$route.params.application
        })
        this.pendingSession = data
        this.recoveryCodes = data.recoveryCodes
        this.phase = 'recovery'
        this.submittedClass = ''
        this.errorMessage = ''
      },
      async onSubmit (event) {

        event.preventDefault()

        if (window.__superturbo_preview_mode) return
        if (this.submittedClass === 'disabled') return

        const failures = recordFailures(this.validationColumns(), this.fieldValues, {
          passwordPolicy: this.passwordPolicy
        })

        if (failures.length) {
          this.errorMessage = failures[0].message
          return
        }

        this.errorMessage = ''
        this.submittedClass = 'disabled'
        this.submitText = 'creating account...'

        try {

          const response = await axios.post(
            `${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/register`,
            this.requestBody()
          )

          if (response.data && response.data.twoFactorSetup) {

            this.phase = 'setup'
            this.setupToken = response.data.setupToken
            this.setupSecret = response.data.secret
            this.otpauthUrl = response.data.otpauthUrl
            this.submittedClass = ''
            this.submitText = 'create account'
            return
          }

          await this.finishSession(response.data)
        } catch (error) {

          this.submittedClass = ''
          this.submitText = 'create account'
          this.errorMessage = (error.response && error.response.data && error.response.data.error) || 'Registration failed'
        }
      },
      async submitFactor (code) {

        if (window.__superturbo_preview_mode) return
        if (this.submittedClass === 'disabled') return

        this.errorMessage = ''
        this.submittedClass = 'disabled'

        try {

          const response = await axios.post(
            `${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/register/authenticator`,
            { setupToken: this.setupToken, code }
          )

          if (response.data && response.data.recoveryCodes && response.data.recoveryCodes.length) {

            this.holdRecoveryCodes(response.data)
            return
          }

          await this.finishSession(response.data)
        } catch (error) {

          this.submittedClass = ''
          this.errorMessage = (error.response && error.response.data && error.response.data.error) || 'That code is not valid'
        }
      }
    }
  }
</script>
